import { Injectable } from '@angular/core';
import { COLOR_SLATE_700 } from '@app/shared/utils/color-utils';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { CHART_SCALE_CONSTANTS } from '../constants/chart-scale.constants';

export interface StackedDatasetLike {
  data?: unknown;
  stack?: string;
}

export function maxValueByStack(datasets: StackedDatasetLike[]): number | null {
  if (datasets.length === 0) {
    return null;
  }

  const firstData = datasets[0]?.data;
  const dataLength = Array.isArray(firstData) ? firstData.length : 0;
  if (dataLength === 0) {
    return null;
  }

  let maxStackedValue = 0;

  for (let index = 0; index < dataLength; index++) {
    const stackSums = new Map<string, number>();

    datasets.forEach(dataset => {
      if (!Array.isArray(dataset.data)) {
        return;
      }
      const value = dataset.data[index];
      if (typeof value !== 'number') {
        return;
      }
      const stackKey = dataset.stack ?? '__default__';
      stackSums.set(stackKey, (stackSums.get(stackKey) ?? 0) + value);
    });

    for (const stackSum of stackSums.values()) {
      if (stackSum > maxStackedValue) {
        maxStackedValue = stackSum;
      }
    }
  }

  return maxStackedValue > 0 ? maxStackedValue : null;
}

@Injectable()
export class ChartConfigService {
  public createBarChartConfig(): ChartConfiguration<'bar'> {
    return {
      type: 'bar',
      data: {
        labels: [],
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              font: {
                size: 14,
                weight: 'normal'
              },
              color: '#333'
            },
            position: 'top',
            align: 'center'
          }
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              font: {
                size: 14,
                weight: 'normal'
              },
              color: '#333'
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              font: {
                size: 14,
                weight: 'normal'
              },
              color: '#333',
              stepSize: 1,
              precision: 0
            }
          }
        }
      },
      plugins: [this.createCustomLabelsPlugin()]
    };
  }

  private createCustomLabelsPlugin(): Plugin<'bar'> {
    return {
      id: 'custom_labels',
      afterDatasetsDraw(chart: Chart): void {
        const ctx = chart.ctx;
        const yAxis = chart.scales?.['y'];

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!chart.isDatasetVisible(datasetIndex)) return;

          meta.data.forEach((bar, index) => {
            const value = dataset.data[index] as number;

            if (value === 0) {
              return;
            }

            if (chart.data.labels && chart.data.labels.length > CHART_SCALE_CONSTANTS.LABEL_COUNT_THRESHOLD) {
              if (value <= CHART_SCALE_CONSTANTS.LABEL_THRESHOLD_MANY) {
                return;
              }
            }

            let stackStart = 0;
            for (let i = 0; i <= datasetIndex; i++) {
              if (chart.isDatasetVisible(i)) {
                stackStart += chart.data.datasets[i].data[index] as number;
              }
            }

            const topY = yAxis.getPixelForValue(stackStart);
            const bottomY = yAxis.getPixelForValue(stackStart - value);
            const centerY = bottomY + (topY - bottomY) / 2;

            ctx.fillStyle = COLOR_SLATE_700;
            ctx.font = '12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const hours = Math.floor(value);
            const minutes = Math.round((value - hours) * 60);
            const formattedValue = minutes === 0 ? `${hours}h` : `${hours}h ${minutes}`;

            ctx.fillText(formattedValue, bar.x, centerY);
          });
        });
      }
    };
  }

  public calculateMaxYScale(maxDataValue: number | null, mode: string, userCount: number): number | undefined {
    if (maxDataValue === null) {
      return this.getFallbackMaxScale(mode, userCount);
    }

    if (mode === 'YEAR') {
      const roundedToHundred =
        Math.ceil(maxDataValue / CHART_SCALE_CONSTANTS.YEAR_SCALE_ROUNDING) * CHART_SCALE_CONSTANTS.YEAR_SCALE_ROUNDING;
      return roundedToHundred + CHART_SCALE_CONSTANTS.YEAR_SCALE_MARGIN;
    }

    const margin = Math.max(
      CHART_SCALE_CONSTANTS.MIN_MARGIN,
      Math.ceil(maxDataValue * CHART_SCALE_CONSTANTS.DEFAULT_MARGIN_PERCENT)
    );
    return Math.ceil(maxDataValue + margin);
  }

  private getFallbackMaxScale(mode: string, userCount: number): number | undefined {
    if (userCount !== 1) {
      return undefined;
    }

    switch (mode) {
      case 'DAY':
        return CHART_SCALE_CONSTANTS.SINGLE_USER_DAY_MAX;
      case 'WEEK':
        return CHART_SCALE_CONSTANTS.SINGLE_USER_WEEK_MAX;
      case 'MONTH':
        return CHART_SCALE_CONSTANTS.SINGLE_USER_MONTH_MAX;
      default:
        return undefined;
    }
  }

  public getMaxStackedValue(chart: Chart): number | null {
    if (!chart?.data?.datasets || chart.data.datasets.length === 0) {
      return null;
    }

    return maxValueByStack(chart.data.datasets);
  }
}
